import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import logo from '../img/torchbearer-logo.png';
import { API_BASE_URL } from '../../config';

const SetupPassword = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [token, setToken] = useState('');
    const [verifying, setVerifying] = useState(true);
    const [userData, setUserData] = useState(null);
    const [verifyError, setVerifyError] = useState('');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const isReset = location.pathname.includes('reset-password');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get('token');
        if (tokenParam) {
            setToken(tokenParam);
            verifyToken(tokenParam);
        } else {
            setVerifying(false);
            setVerifyError('Setup token is missing.');
        }
    }, [location.search]);

    const verifyToken = async (tokenValue) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/app-user/verify-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ token: tokenValue }),
            });

            const data = await response.json();

            if (data.success) {
                setUserData({
                    name: data.name,
                    email: data.email
                });
            } else {
                setVerifyError(data.message || 'Invalid or expired setup token.');
            }
        } catch (err) {
            setVerifyError('Failed to verify token. Please try again later.');
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/app-user/setup-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                    password: password,
                    password_confirmation: confirmPassword
                }),
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                if (data.errors) {
                    setFieldErrors(data.errors);
                    setError('Please correct the errors below.');
                } else {
                    setError(data.message || 'Failed to set password.');
                }
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-amber-50 via-white to-orange-50">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 text-amber-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-semibold text-lg animate-pulse">Verifying secure setup link...</p>
                </div>
            </div>
        );
    }

    if (verifyError) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-50 to-white">
                <div className="bg-white border-t-8 border-red-500 p-8 rounded-2xl shadow-2xl max-w-md w-full text-center text-gray-800">
                    <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-12 h-12 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-8">{verifyError}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="w-full bg-gray-800 text-white py-4 rounded-xl font-bold hover:bg-gray-900 transition-all shadow-lg hover:shadow-xl active:scale-95 text-gray-800"
                    >
                        Return to Homepage
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-green-50 to-white">
                <div className="bg-white border-t-8 border-green-500 p-10 rounded-2xl shadow-2xl max-w-md w-full text-center">
                    <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                        <CheckCircle className="w-16 h-16 text-green-500" />
                    </div>
                    <h2 className="text-3xl font-extrabold text-gray-800 mb-4">{isReset ? 'Password Reset!' : "You're All Set!"}</h2>
                    <p className="text-gray-600 mb-8 text-lg">{isReset ? 'Your password has been successfully reset. You can now login using your new password on the mobile app.' : 'Your password has been successfully configured. You can now access your account via the mobile app.'}</p>
                    <div className="p-6 bg-amber-50 rounded-2xl border border-amber-100 text-sm text-gray-700 mb-8 text-gray-800">
                        <p className="font-semibold mb-1">Account Identifier:</p>
                        <p className="text-amber-700 font-bold text-base">{userData.email}</p>
                    </div>
                    <button
                        onClick={() => navigate('/')}
                        className="w-full bg-amber-600 text-white py-4 rounded-xl font-bold hover:bg-amber-700 transition-all shadow-lg hover:shadow-xl active:scale-95"
                    >
                        Go to Institute Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-xl mx-auto">
                <div className="text-center mb-12">
                    <div className="flex justify-center mb-6 transform hover:scale-110 transition-transform duration-300">
                        <img src={logo} alt="Torchbearer Logo" className="h-24 w-auto drop-shadow-md" />
                    </div>
                    <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
                        {isReset ? 'Reset Your Password' : 'Setup Your Account'}
                    </h2>
                    <div className="mt-4 flex items-center justify-center space-x-2">
                        <span className="h-px w-8 bg-amber-300"></span>
                        <p className="text-lg text-gray-600">
                            Welcome back, <span className="font-bold text-amber-600">{userData.name}</span>
                        </p>
                        <span className="h-px w-8 bg-amber-300"></span>
                    </div>
                </div>

                <div className="bg-white py-12 px-8 sm:px-12 shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-[2.5rem] border border-gray-100 relative overflow-hidden">
                    {/* Decorative element */}
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-amber-100 rounded-full opacity-50 blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-32 h-32 bg-orange-100 rounded-full opacity-50 blur-2xl"></div>

                    {error && (
                        <div className="mb-8 p-5 bg-red-50 border-l-4 border-red-500 rounded-r-xl text-red-700 text-sm flex items-start shadow-sm">
                            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="font-medium">{error}</span>
                        </div>
                    )}

                    <form className="space-y-8 relative z-10" onSubmit={handleSubmit}>
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">
                                Email Address
                            </label>
                            <div className="group transition-all">
                                <input
                                    type="email"
                                    value={userData.email}
                                    readOnly
                                    className="block w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-500 cursor-not-allowed focus:outline-none shadow-inner"
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-400 ml-1 italic font-medium font-poppins">This is your registered account email.</p>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">
                                New Password
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={`block w-full pl-12 pr-12 py-4 border-2 ${fieldErrors.password ? 'border-red-300 focus:border-red-500' : 'border-gray-100 focus:border-amber-500'} rounded-2xl focus:ring-4 focus:ring-amber-500/10 outline-none transition-all shadow-sm group-hover:border-gray-200 bg-gray-50/50 focus:bg-white`}
                                    placeholder="Create a strong password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-400 hover:text-amber-600 transition-colors focus:outline-none"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {fieldErrors.password && (
                                <p className="mt-2 text-xs font-bold text-red-500 ml-1">{fieldErrors.password[0]}</p>
                            )}
                            <div className="flex items-center space-x-2 ml-1 mt-2">
                                <div className={`h-1 flex-1 rounded-full transition-all ${password.length >= 8 ? 'bg-green-500' : password.length > 0 ? 'bg-amber-400' : 'bg-gray-200'}`}></div>
                                <div className={`h-1 flex-1 rounded-full transition-all ${password.length >= 12 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                                <div className={`h-1 flex-1 rounded-full transition-all ${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                            </div>
                            <p className="mt-2 text-xs text-gray-500 ml-1">Minimum 8 characters with numbers and symbols recommended.</p>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">
                                Confirm Password
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="block w-full pl-12 py-4 border-2 border-gray-100 rounded-2xl focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 outline-none transition-all shadow-sm group-hover:border-gray-200 bg-gray-50/50 focus:bg-white"
                                    placeholder="Repeat your password"
                                />
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full flex justify-center items-center py-5 px-6 border border-transparent rounded-2xl shadow-xl text-xl font-bold text-white bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed`}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                                        <span>Saving Password...</span>
                                    </>
                                ) : (
                                    <span>{isReset ? 'Reset My Password' : 'Complete Account Setup'}</span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="mt-12 text-center">
                    <p className="text-gray-500 text-sm font-medium">
                        &copy; {new Date().getFullYear()} Torchbearer Institute of Technologies
                    </p>
                    <div className="mt-2 flex justify-center space-x-4 text-xs text-amber-600 font-bold uppercase tracking-widest">
                        <a href="#" className="hover:underline">Privacy Policy</a>
                        <span className="text-gray-300">•</span>
                        <a href="#" className="hover:underline">Terms of Service</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetupPassword;
